import {useEffect,useRef} from 'react';
import {CONSUMER_COLORS} from './ConsumerTheme.js';

// Subtle "swimmer passing by" pointer effect: moving the mouse (or dragging
// a finger) over the parent leaves a wake - two thin lines that open up
// behind the path like a V and weave gently with a stroke rhythm, plus a
// faint centre line. No shapes, no splash; everything fades within a second.
// Renders a canvas that fills its parent - the parent must be positioned
// (relative) and its content should sit above (position:relative).
// Never intercepts pointer events; disabled under prefers-reduced-motion.

const WAKE_LIFE_MS=950;      // how long a point of the wake lives
const MIN_STEP=4;            // px between two recorded wake points
const SPREAD_START=3;        // px half-width of the wake at the swimmer
const SPREAD_END=26;         // px half-width when a point fades out
const STROKE_LENGTH=110;     // px travelled per stroke cycle (weave period)
const WEAVE=3;               // px of side-to-side weave per stroke

const hexToRgb=(hex)=>{
    const n=parseInt(hex.replace('#',''),16);
    return `${(n>>16)&255},${(n>>8)&255},${n&255}`;
};

const WaterRipples=({color=CONSUMER_COLORS.cyan,intensity=0.22})=>{
    const canvasRef=useRef(null);

    useEffect(()=>{
        const canvas=canvasRef.current;
        const host=canvas?.parentElement;
        if(!canvas||!host){return;}
        if(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches){return;}

        const ctx=canvas.getContext('2d');
        const rgb=hexToRgb(color);
        let wake=[];         // {x,y,nx,ny,dist,born}
        let last=null;       // last recorded point
        let dir=null;        // smoothed swim direction {x,y}
        let dist=0;          // px travelled, drives the weave
        let frame=0;

        const resize=()=>{
            const dpr=window.devicePixelRatio||1;
            const {width,height}=host.getBoundingClientRect();
            canvas.width=Math.round(width*dpr);
            canvas.height=Math.round(height*dpr);
            ctx.setTransform(dpr,0,0,dpr,0,0);
        };
        resize();
        const observer=new ResizeObserver(resize);
        observer.observe(host);

        // Point on one side of the wake (side = 1 left, -1 right)
        const edge=(p,t,side)=>{
            const weave=Math.sin(p.dist/STROKE_LENGTH*Math.PI*2)*WEAVE*side;
            const spread=SPREAD_START+(SPREAD_END-SPREAD_START)*Math.sqrt(t)+weave;
            return {x:p.x+p.nx*spread*side,y:p.y+p.ny*spread*side};
        };

        const draw=(now)=>{
            const {width,height}=host.getBoundingClientRect();
            ctx.clearRect(0,0,width,height);
            wake=wake.filter((p)=>now-p.born<WAKE_LIFE_MS);

            ctx.lineCap='round';
            for(let i=1;i<wake.length;i++){
                const prev=wake[i-1];
                const cur=wake[i];
                const tPrev=(now-prev.born)/WAKE_LIFE_MS;
                const tCur=(now-cur.born)/WAKE_LIFE_MS;
                const fade=Math.pow(1-tCur,2);
                for(const side of [1,-1]){
                    const a=edge(prev,tPrev,side);
                    const b=edge(cur,tCur,side);
                    ctx.lineWidth=1.1;
                    ctx.strokeStyle=`rgba(${rgb},${intensity*fade})`;
                    ctx.beginPath();
                    ctx.moveTo(a.x,a.y);
                    ctx.lineTo(b.x,b.y);
                    ctx.stroke();
                }
                ctx.lineWidth=1;
                ctx.strokeStyle=`rgba(255,255,255,${intensity*0.35*fade})`;
                ctx.beginPath();
                ctx.moveTo(prev.x,prev.y);
                ctx.lineTo(cur.x,cur.y);
                ctx.stroke();
            }
            frame=wake.length?requestAnimationFrame(draw):0;
        };

        const onMove=(e)=>{
            const rect=host.getBoundingClientRect();
            const p={x:e.clientX-rect.left,y:e.clientY-rect.top};
            if(!last){last=p;return;}
            const dx=p.x-last.x;
            const dy=p.y-last.y;
            const step=Math.hypot(dx,dy);
            if(step<MIN_STEP){return;}
            // Smooth the direction so the wake doesn't jitter on small wiggles
            const ux=dx/step;
            const uy=dy/step;
            dir=dir?{x:dir.x*0.7+ux*0.3,y:dir.y*0.7+uy*0.3}:{x:ux,y:uy};
            const len=Math.hypot(dir.x,dir.y)||1;
            dist+=step;
            wake.push({x:p.x,y:p.y,nx:-dir.y/len,ny:dir.x/len,dist,born:performance.now()});
            last=p;
            if(!frame){frame=requestAnimationFrame(draw);}
        };
        const onLeave=()=>{last=null;dir=null;};

        host.addEventListener('pointermove',onMove,{passive:true});
        host.addEventListener('pointerleave',onLeave,{passive:true});
        return ()=>{
            host.removeEventListener('pointermove',onMove);
            host.removeEventListener('pointerleave',onLeave);
            observer.disconnect();
            if(frame){cancelAnimationFrame(frame);}
        };
    },[color,intensity]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0}}
        />
    );
};

export default WaterRipples;
